import { useEffect, useState } from 'react';
import { listEvents } from '@lib/care-api';
import { currentClient, useClient } from '@lib/clients';
import { CLOSED_STATUSES } from '@lib/opt-in-lane';
import type { ListResp } from '@lib/care-model';
import {
  cachedAnswer, queueCacheKey, readFailed, readOutcome, readStarted, readSucceeded, subscribeQueueCache,
  type QueueShape,
} from '@lib/queue-cache';

/**
 * THE REAL COUNT on 01 Inbound queue in the left column (row 866, William 4 Oct 2026: "Show the real
 * count").
 *
 * Until row 866 the number on that line was the SMS unread count (lib/sms-inbound.tsx): the distinct
 * phone numbers that had texted since this browser started watching, cleared only one member at a
 * time as each conversation was opened. It ran into the thousands (his "3k"; the line printed the raw
 * number, no formatting) and said nothing about the work in the queue.
 *
 * WHAT IT COUNTS: the open entries the Inbound queue page lists with its default filters: inbound,
 * every status the server does not treat as closed, no search, the call list and the bad-number
 * entries left out, the fax-failure notices left out. Open means not in CLOSED_STATUSES (the copy of
 * the API's TERMINAL_STATUSES, event-ops.ts), so new, in progress, actioned, in review and responded
 * all count. Entries, not threads: the page's grouped view folds a member's entries into one row.
 *
 * WHERE THE NUMBER COMES FROM: the answers the Inbound queue page itself reads (lib/queue-cache.ts).
 * While the page is open it reads every 20 s and the column asks the server for nothing. Anywhere
 * else the column asks the server itself, AT MOST ONCE A MINUTE, only while the tab is visible, with
 * the page's own shape, so opening the queue afterwards renders at once from that same answer.
 *
 * WHEN IT SHOWS NOTHING: before the first answer, whenever the newest read failed, and when the newest
 * answer is older than five minutes (a tab back from a long sleep, say). Never a stale or a guessed
 * number. A queue with nothing open shows no badge either.
 */

/** The page's view switch (pages/InboundQueue.tsx VIEW_STORAGE_KEY): the grouped view unless 'flat'. */
export const INBOUND_VIEW_KEY = 'membercare.inbound.view';

/**
 * The Inbound queue page's default read, as the server sees it (pages/InboundQueue.tsx, its
 * useEventQueue call with nothing chosen: every status, no search, BAD_NUMBER_HIDDEN, the fax failures
 * left out). The grouped view adds groupBy=thread; the entries are the same either way. Pinned in
 * lockstep with the page's real request by tests/integration/real-count.test.tsx.
 */
export const INBOUND_DEFAULT_SHAPE: Readonly<QueueShape> = {
  direction: 'inbound',
  status: undefined,
  q: '',
  notSource: ['out-calling', 'bad-number'],
  excludeFaxFailures: true,
};

/** The column asks the server itself at most once in this long (and never while anyone else is asking). */
export const OPEN_COUNT_READ_EVERY_MS = 60_000;
/** How often the column looks at the shared answers (cheap: no request unless one is due). */
export const OPEN_COUNT_LOOK_EVERY_MS = 15_000;
/** An answer older than this is not shown at all. */
export const OPEN_COUNT_MAX_AGE_MS = 5 * 60_000;

export type InboundView = 'thread' | 'flat';

export function inboundShape(view: InboundView): QueueShape {
  return view === 'flat' ? { ...INBOUND_DEFAULT_SHAPE } : { ...INBOUND_DEFAULT_SHAPE, groupBy: 'thread' };
}

/** The view the page will open in, read exactly as the page reads it. */
export function rememberedInboundView(): InboundView {
  try {
    return localStorage.getItem(INBOUND_VIEW_KEY) === 'flat' ? 'flat' : 'thread';
  } catch {
    return 'thread'; // storage blocked: the page falls back to the grouped view too
  }
}

/** The cache keys of the page's two default reads (grouped, flat); either one carries the entries. */
export function inboundKeys(client: string): string[] {
  return [queueCacheKey(client, inboundShape('thread')), queueCacheKey(client, inboundShape('flat'))];
}

/** Open entries in an answer: every one whose status the server does not treat as closed. */
export function openCountOf(resp: Pick<ListResp, 'items'>): number {
  let n = 0;
  for (const r of resp.items ?? []) if (!CLOSED_STATUSES.has(r.status)) n += 1;
  return n;
}

/** The badge's tooltip, in plain words. */
export function openCountWords(n: number): string {
  return `${n.toLocaleString('en-US')} open item${n === 1 ? '' : 's'} in the Inbound queue`;
}

/**
 * The count to show now, or null when it cannot be vouched for: no answer yet, the newest read of the
 * page's default shapes failed, or the newest answer is older than OPEN_COUNT_MAX_AGE_MS.
 */
export function inboundOpenCount(client: string, now: number = Date.now()): number | null {
  let newest: { key: string; at: number; failed: boolean } | null = null;
  for (const key of inboundKeys(client)) {
    const o = readOutcome(key);
    if (o.settledAt === null) continue;
    if (!newest || o.settledAt > newest.at) newest = { key, at: o.settledAt, failed: o.failed };
  }
  if (!newest || newest.failed) return null;
  if (now - newest.at > OPEN_COUNT_MAX_AGE_MS) return null;
  const resp = cachedAnswer(newest.key);
  return resp ? openCountOf(resp) : null;
}

/**
 * Whether the column should ask the server itself: nobody is asking right now (the page announces its
 * read before it goes out), and nobody has had an answer, good or bad, in the last minute.
 */
export function openCountReadDue(client: string, now: number = Date.now()): boolean {
  let newest: number | null = null;
  for (const key of inboundKeys(client)) {
    const o = readOutcome(key);
    if (o.inflight > 0) return false;
    if (o.settledAt !== null && (newest === null || o.settledAt > newest)) newest = o.settledAt;
  }
  return newest === null || now - newest >= OPEN_COUNT_READ_EVERY_MS;
}

/**
 * The column's own read: the page's shape for the view it will open in, into the same cache.
 *
 * ONLY FOR THE PLAN SELECTED NOW (QA-1217, 5 Oct 2026): listEvents names currentClient() while the
 * answer is filed under `client`, the plan the hook last rendered, so a read for a plan that is no
 * longer the selection does not run (lib/folder-counts.ts readFolderCounts says why in full).
 */
export async function readInboundOpenCount(client: string): Promise<void> {
  if (currentClient() !== client) return;
  const shape = inboundShape(rememberedInboundView());
  const key = queueCacheKey(client, shape);
  const ticket = readStarted(key);
  try {
    readSucceeded(key, ticket, await listEvents(shape));
  } catch {
    readFailed(key, ticket);
  }
}

/** React hook: the Inbound queue's open count for the left column, or null to show nothing. */
export function useInboundOpenCount(): number | null {
  const client = useClient();
  const [count, setCount] = useState<number | null>(() => inboundOpenCount(client));
  useEffect(() => {
    const look = (): void => setCount(inboundOpenCount(client));
    const tick = (): void => {
      look();
      if (document.visibilityState === 'visible' && openCountReadDue(client)) void readInboundOpenCount(client);
    };
    const stop = subscribeQueueCache(look);
    // Runs after the page's own effects (a child's effects run before its parent's), so on the queue
    // page the page's first read is already announced and this asks the server for nothing.
    tick();
    const timer = setInterval(tick, OPEN_COUNT_LOOK_EVERY_MS);
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stop();
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [client]);
  return count;
}
